import { useMemo, useState } from "react";
import { Download, FileArchive, FileSpreadsheet, Printer, ShieldCheck } from "lucide-react";

import { FieldError } from "@/components/field-error";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  organisationExportWorkspace,
  recordOrganisationExport,
} from "@/lib/api";
import {
  buildCsvZip,
  exportPackToExcelXml,
  sha256Hex,
  type OrganisationExportPack,
} from "@/lib/export-pack";
import type { Organisation } from "@/lib/types";
import { titleCase } from "@/lib/utils";

type ExportFormat = "json" | "csv_zip" | "excel_xml" | "print_pdf";

function saveFile(name: string, content: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function html(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function ExportCenter({ organisation }: { organisation: Organisation }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<ExportFormat>();
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ sha256: string; counts: Record<string, number> }>();
  const [availableModules, setAvailableModules] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const validReason = reason.trim().length >= 10;
  const selected = useMemo(() => new Set(selectedModules), [selectedModules]);

  async function generate(format: ExportFormat) {
    const printWindow = format === "print_pdf" ? window.open("", "_blank") : null;
    setBusy(format);
    setError("");
    try {
      const workspace = await organisationExportWorkspace(organisation.id);
      const moduleNames = Object.keys(workspace.modules);
      if (!availableModules.length) {
        setAvailableModules(moduleNames);
        setSelectedModules(moduleNames);
      }
      const scope = selectedModules.length ? selectedModules : moduleNames;
      const modules = Object.fromEntries(
        Object.entries(workspace.modules).filter(([name]) => scope.includes(name)),
      );
      const recordCounts = Object.fromEntries(
        Object.entries(modules).map(([name, rows]) => [name, rows.length]),
      );
      const pack: OrganisationExportPack = {
        format: "raqoz.organisation-export",
        version: "3.3.0",
        generated_at: new Date().toISOString(),
        organisation: workspace.organisation,
        modules,
        record_counts: recordCounts,
        boundaries: {
          bank_execution: false,
          customer_references: false,
          attachments: "Attachment metadata is included. Private file contents remain in governed storage.",
        },
      };
      const stem = `raqoz-${organisation.slug}-${pack.generated_at.slice(0, 10)}`;
      let digest: string;
      if (format === "json") {
        const content = JSON.stringify(pack, null, 2);
        digest = await sha256Hex(content);
        saveFile(`${stem}.json`, content, "application/json;charset=utf-8");
      } else if (format === "csv_zip") {
        const content = buildCsvZip(modules);
        digest = await sha256Hex(content);
        saveFile(`${stem}-registers.zip`, content, "application/zip");
      } else if (format === "excel_xml") {
        const content = exportPackToExcelXml(pack);
        digest = await sha256Hex(content);
        saveFile(`${stem}-workbook.xml`, content, "application/vnd.ms-excel");
      } else {
        const summary = Object.entries(recordCounts)
          .map(([name, count]) => `<tr><th scope="row">${html(titleCase(name))}</th><td>${count}</td></tr>`)
          .join("");
        const content = `<!doctype html><html><head><meta charset="utf-8"><title>${html(stem)}</title><style>body{font-family:Arial,sans-serif;margin:40px;line-height:1.45}table{border-collapse:collapse;width:100%}th,td{border:1px solid;padding:8px;text-align:left}small{display:block;margin-top:24px}</style></head><body><h1>RAQOZ organisation export</h1><p><strong>${html(organisation.name)}</strong></p><p>Generated ${html(pack.generated_at)} for: ${html(reason.trim())}</p><table><thead><tr><th>Module</th><th>Records</th></tr></thead><tbody>${summary}</tbody></table><small>This cover sheet accompanies the governed digital export. RAQOZ records payment evidence but does not execute bank payments.</small></body></html>`;
        digest = await sha256Hex(content);
        if (!printWindow) throw new Error("Allow the print window, then try again.");
        printWindow.document.open();
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
      await recordOrganisationExport(organisation.id, {
        scope,
        format,
        reason: reason.trim(),
        recordCounts,
        sha256: digest,
      });
      setResult({ sha256: digest, counts: recordCounts });
    } catch (reasonValue) {
      printWindow?.close();
      setError((reasonValue as Error).message || "Unable to generate this export.");
    } finally {
      setBusy(undefined);
    }
  }

  function toggle(module: string) {
    setSelectedModules((current) =>
      current.includes(module)
        ? current.filter((item) => item !== module)
        : [...current, module],
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="DATA PORTABILITY"
        title="Export Centre"
        description={`${organisation.name} · Download governed operational registers and the evidence chain without losing record provenance.`}
      />
      <section className="boundary-panel" aria-labelledby="export-boundary">
        <ShieldCheck className="size-5" aria-hidden="true" />
        <div>
          <h2 id="export-boundary">Export boundary</h2>
          <p>Every export is tenant-scoped, SHA-256 recorded and written to the immutable audit trail. Private attachment contents stay in controlled storage; their metadata and source paths are included.</p>
        </div>
      </section>
      <div className="grid gap-5 xl:grid-cols-[1fr_.8fr]">
        <Panel>
          <h2 className="text-xl font-semibold">Generate a complete operating record</h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">The first generation discovers every available module. You can then narrow later exports to a specific scope.</p>
          <Label className="mt-5 block">
            Business reason for export
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={10}
              placeholder="Example: month-end finance archive and auditor evidence handover"
            />
          </Label>
          {availableModules.length > 0 && (
            <fieldset className="mt-5">
              <legend className="text-sm font-semibold">Included modules</legend>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {availableModules.map((module) => (
                  <Label className="flex min-h-11 items-center gap-3 rounded-lg border border-[var(--border)] px-3" key={module}>
                    <input type="checkbox" checked={selected.has(module)} onChange={() => toggle(module)} />
                    {titleCase(module)}
                  </Label>
                ))}
              </div>
            </fieldset>
          )}
          <FieldError>{error}</FieldError>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <Button disabled={!validReason || Boolean(busy)} onClick={() => generate("json")}><Download className="size-4" />{busy === "json" ? "Preparing…" : "Evidence JSON"}</Button>
            <Button variant="outline" disabled={!validReason || Boolean(busy)} onClick={() => generate("csv_zip")}><FileArchive className="size-4" />{busy === "csv_zip" ? "Preparing…" : "CSV register ZIP"}</Button>
            <Button variant="outline" disabled={!validReason || Boolean(busy)} onClick={() => generate("excel_xml")}><FileSpreadsheet className="size-4" />{busy === "excel_xml" ? "Preparing…" : "Excel workbook"}</Button>
            <Button variant="outline" disabled={!validReason || Boolean(busy)} onClick={() => generate("print_pdf")}><Printer className="size-4" />{busy === "print_pdf" ? "Preparing…" : "Print / save PDF"}</Button>
          </div>
        </Panel>
        <Panel>
          <h2 className="text-xl font-semibold">Recorded evidence</h2>
          {!result ? (
            <p className="mt-3 text-sm text-[var(--text-secondary)]">Generate an export to see its module coverage and integrity digest.</p>
          ) : (
            <>
              <dl className="mt-4 grid gap-2 text-sm">
                {Object.entries(result.counts).map(([module, count]) => (
                  <div className="flex justify-between gap-4 border-b border-[var(--border)] py-2" key={module}>
                    <dt>{titleCase(module)}</dt><dd className="tabular-nums font-semibold">{count}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-5 text-xs text-[var(--text-secondary)]">SHA-256</p>
              <code className="mt-1 block break-all text-xs">{result.sha256}</code>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
